import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat } from 'app/lib/date';
import { type useUserCurrency } from 'app/network/user';
import { Trans } from 'react-i18next';
import { IconCheckbox } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { buildTextPlan } from '../my-plan-utils';
import { theme } from './shared';
import type { ActivePlan } from './shared';

export const ActivePlanDetails = ({
  activePlan,
  onStopRecurrence,
  userCurrency,
  variant,
}: {
  activePlan: ActivePlan;
  onStopRecurrence: () => void;
  userCurrency: ReturnType<typeof useUserCurrency>;
  variant: {
    billing: 'recurring' | 'recurrence-available' | 'other';
    layout: 'large' | 'compact';
    lifecycle: 'free-trial' | 'standard';
  };
}) => {
  const navigation = useNavigation();
  const { t } = useScopedTranslation();
  const planCode = activePlan.code.code;
  const planName = activePlan.code.name;
  const planRecurrence = activePlan.recurrence;
  const groupSubtype = activePlan.code.group?.subtype;
  const isB2CWithRecurrence = variant.billing === 'recurring';
  const isB2CWithoutRecurrence = variant.billing === 'recurrence-available';
  const isOnFreeTrial = variant.lifecycle === 'free-trial';

  return (
    <Column
      gap="spacing.general.x4"
      marginTop={variant.layout === 'large' ? 'spacing.general.0' : 'spacing.general.x4'}
    >
      <Row alignItems="center" gap="spacing.general.x4">
        {isB2CWithRecurrence ? <IconCheckbox strokeWidth={ICON_STROKE_WIDTH} /> : undefined}
        <Text fontWeight="700" variant="headerXSmall">
          <Trans
            components={{ strong: <Text fontSize={20} fontWeight="700" /> }}
            i18nKey={buildTextPlan('title', planCode, groupSubtype, planRecurrence)}
            ns="my-plan"
            values={{ planName }}
          />
        </Text>
      </Row>

      <Text variant="bodyBase">
        <Trans
          components={{ strong: <Text fontWeight="700" variant="bodyBase" /> }}
          i18nKey={buildTextPlan('firstParagraph', planCode, groupSubtype, planRecurrence)}
          ns="my-plan"
          values={{ planName }}
        />
      </Text>

      {isB2CWithRecurrence ? (
        <Text fontWeight="700" variant="bodyBase">
          {t(`price.${activePlan.recurrence}.${userCurrency}`, { ns: 'payment' })}
          {' / '}
          {t(`period.${activePlan.recurrence}`, { ns: 'payment' })}
        </Text>
      ) : undefined}

      <Separator color={theme.colors['border.secondary']} />

      <Text variant="bodyBase">
        {t(isB2CWithRecurrence ? 'recurrenceWhen' : isOnFreeTrial ? 'trialEndsWhen' : 'endsWhen')}
      </Text>
      <Text fontWeight="700" variant="bodyBase">
        {activePlan.expiresAt
          ? localeAwareFormat(new Date(activePlan.expiresAt), t('dateFormat_MMMMdYYYY', { ns: 'common' }))
          : t('noEnd')}
      </Text>

      {isB2CWithRecurrence ? (
        <Button onPress={onStopRecurrence} variant="secondary">
          {t('stopRecurrenceButtonText')}
        </Button>
      ) : undefined}

      {isB2CWithoutRecurrence ? (
        <Button onPress={() => navigation.navigate('payment', undefined, { pop: true })} variant="secondary">
          {t('startRecurrenceButtonText')}
        </Button>
      ) : undefined}
    </Column>
  );
};
